import { isValidPluginPermission, type PluginPermission } from '@fias/arche-sdk';
import type { VirtualFileSystem } from '../types';

/**
 * Extract the plugin's declared permissions from its in-progress
 * `fias-plugin.json` manifest in the builder's virtual file system.
 *
 * This is what the manifest LITERALLY SAYS — no longer the set the preview
 * enforces. That set is `resolveBuilderPluginPermissions`, which lives
 * server-side (it derives permissions from SDK hook call-sites, which needs
 * `@fias/db-types` — a package the webapp deliberately does not depend on) and
 * arrives with each build response as `permissions`.
 *
 * Why the split: a builder manifest is GENERATED, not authored. `publish`
 * rebuilds it from source + settings, so the declared array is an input to the
 * resolution, never the authority. Enforcing it alone made the preview stricter
 * than production — `useFiasUser()` without a declared `user:profile:read`
 * crashed the preview and would have published fine (rpt_0c414862).
 *
 * This function remains the FALLBACK for the window before the first build
 * response of a session. It is a strict subset of the resolved set, so it can
 * only ever be more restrictive — it may surface a denial the real preview
 * would not, and can never grant something publish would withhold. Any failure
 * (missing file, unparseable JSON, wrong shape) yields `[]`.
 */
export function extractDeclaredPermissions(files: VirtualFileSystem | null): PluginPermission[] {
  const manifest = files?.['fias-plugin.json'];
  if (!manifest?.content) return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(manifest.content);
  } catch {
    return [];
  }

  if (!parsed || typeof parsed !== 'object') return [];
  const raw = (parsed as { permissions?: unknown }).permissions;
  if (!Array.isArray(raw)) return [];

  const seen = new Set<PluginPermission>();
  for (const entry of raw) {
    if (typeof entry === 'string' && isValidPluginPermission(entry)) {
      seen.add(entry);
    }
  }
  return [...seen];
}

/** Shape the preview store's mock catalog uses (PluginBridgeOptions.previewStoreProducts). */
export interface DeclaredPreviewProduct {
  productIdentifier: string;
  type: string;
  name: string;
  description: string | null;
  priceCredits: number;
  metadata: Record<string, unknown>;
  subscriptionPeriod?: string;
}

/**
 * Extract the manifest's declared `products` for the preview store's mock
 * catalog, so `useFiasStore()` shows the plugin's real products in the
 * builder preview (purchases stay simulated — no credits move, no
 * entitlement persists). Without this the preview catalog was empty AND the
 * old purchase stub succeeded anyway — a purchase of an undeclared product
 * "worked" with no confirmation UI (kitchen-sink finding, 2026-07-28).
 *
 * Same tolerance posture as extractDeclaredPermissions: missing/unparseable
 * manifest or malformed entries yield an empty/partial catalog, which the
 * store stubs then represent honestly (empty products, PRODUCT_NOT_FOUND).
 */
export function extractDeclaredProducts(files: VirtualFileSystem | null): DeclaredPreviewProduct[] {
  const manifest = files?.['fias-plugin.json'];
  if (!manifest?.content) return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(manifest.content);
  } catch {
    return [];
  }

  if (!parsed || typeof parsed !== 'object') return [];
  const raw = (parsed as { products?: unknown }).products;
  if (!Array.isArray(raw)) return [];

  const products: DeclaredPreviewProduct[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const p = entry as Record<string, unknown>;
    if (typeof p.identifier !== 'string' || typeof p.name !== 'string') continue;
    products.push({
      productIdentifier: p.identifier,
      type: typeof p.type === 'string' ? p.type : 'consumable',
      name: p.name,
      description: typeof p.description === 'string' ? p.description : null,
      priceCredits: typeof p.priceCredits === 'number' ? p.priceCredits : 0,
      metadata: {},
      ...(typeof p.subscriptionPeriod === 'string'
        ? { subscriptionPeriod: p.subscriptionPeriod }
        : {}),
    });
  }
  return products;
}
